/**
 * Content validation utilities for rich text content
 * Handles HTML content validation from rich text editors like Jodit Pro
 */

/**
 * Checks if HTML content has meaningful text content
 * @param {string} content - HTML content to validate
 * @returns {boolean} - true if content has meaningful text, false otherwise
 */
export const hasMeaningfulContent = (content) => {
  if (!content) return false;
  try {
    // Use DOM to parse HTML safely and extract visible text
    const container = document.createElement('div');
    container.innerHTML = content;
    // textContent decodes entities and ignores tags
    const text = (container.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 0;
  } catch (_) {
    // Fallback to simple check if DOM not available
    const fallback = String(content)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return fallback.length > 0;
  }
};

/**
 * Gets the plain text content from HTML
 * @param {string} content - HTML content
 * @returns {string} - Plain text content
 */
export const getPlainTextContent = (content) => {
  if (!content) return '';
  try {
    const container = document.createElement('div');
    container.innerHTML = content;
    return (container.textContent || '').replace(/\s+/g, ' ').trim();
  } catch (_) {
    return String(content)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
};

/**
 * Validates notes form data
 * @param {Object} formData - Form data object
 * @param {string} formData.title - Notes title
 * @param {string} formData.content - Notes content (HTML)
 * @param {string|number} formData.sequence - Notes sequence
 * @param {string|number} formData.time - Notes time
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateNotesForm = (formData) => {
  const { title, content, sequence, time } = formData;
  const errors = [];
  
  if (!title || !title.trim()) {
    errors.push('Title is required');
  }
  
  if (!hasMeaningfulContent(content)) {
    errors.push('Content is required');
  }
  
  if (!sequence || sequence === '' || sequence === null || sequence === undefined) {
    errors.push('Sequence is required');
  } else if (parseInt(sequence) <= 0) {
    errors.push('Sequence must be a positive number');
  }
  
  if (time === '' || time === null || time === undefined) {
    errors.push('Time is required');
  } else if (parseInt(time) < 0) {
    errors.push('Time must be a non-negative number');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

